import { ChartSeries, SERIES_COLORS } from "@nova/component-charts"
import { buildBucketAxis, padToAxis } from "@nova/queryable"
import { EventCategory } from "@nova/schemas"
import { useMemo } from "react"
import useSWRInfinite from "swr/infinite"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { ActivityFilters, compileActivityFilters, kindsFor } from "./activityFilters"
import { selectAppliedFilters } from "./slice"
import {
    createActivityQuery,
    fetchActivityTimeline,
    TimelineCursor,
    useActivityBatchQuery,
} from "./ActivityQueries"

type SeriesKey = "layers" | "comments" | "pdfs" | "people"

type SeriesConfig = {
    key: SeriesKey
    label: string
    category: EventCategory
    color: string
}

export type ActivitySeriesTotal = SeriesConfig & { total: number }

const ACTIVITY_SERIES: SeriesConfig[] = [
    { key: "layers", label: "Layers", category: EventCategory.Layers, color: SERIES_COLORS.blue },
    {
        key: "comments",
        label: "Comments",
        category: EventCategory.Comments,
        color: SERIES_COLORS.violet,
    },
    { key: "pdfs", label: "PDFs", category: EventCategory.Pdfs, color: SERIES_COLORS.red },
    { key: "people", label: "People", category: EventCategory.Access, color: SERIES_COLORS.green },
]

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

export const useActivityChart = () => {
    const applied = useAppSelector(selectAppliedFilters)

    const { baseFilters, start, end, unit } = useMemo(
        () => compileActivityFilters(applied),
        [applied]
    )

    const queries = useMemo(() => {
        const selectedCategories = Object.keys(applied.categories) as EventCategory[]
        const timezone = browserTimezone()

        const seriesQuery = (category: EventCategory) => {
            const selection = applied.categories[category]
            const excluded = selectedCategories.length > 0 && !selection

            const kindFilter = {
                field: "kind",
                op: "IN",
                value: excluded ? [] : kindsFor(category, selection?.actions ?? []),
            } as const

            const subjectFilter =
                selection && selection.subjectTypes.length > 0
                    ? [{ field: "subject_type", op: "IN", value: selection.subjectTypes } as const]
                    : []

            return createActivityQuery({
                timeBuckets: [{ field: "created_at", unit, alias: "bucket", timezone } as const],
                aggregates: [{ field: "id", func: "COUNT", alias: "total" } as const],
                filters: [...baseFilters, kindFilter, ...subjectFilter],
            })
        }

        return {
            layers: seriesQuery(EventCategory.Layers),
            comments: seriesQuery(EventCategory.Comments),
            pdfs: seriesQuery(EventCategory.Pdfs),
            people: seriesQuery(EventCategory.Access),
        }
    }, [applied, baseFilters, unit])

    const { data, isLoading, error } = useActivityBatchQuery(queries)

    const series = useMemo<ChartSeries[]>(() => {
        const axis = buildBucketAxis(start, end, unit)

        return ACTIVITY_SERIES.map((config) => {
            const rows = data?.[config.key] ?? []
            return {
                key: config.key,
                label: config.label,
                color: config.color,
                points: padToAxis(
                    axis,
                    rows.map((row) => ({ bucket: row.bucket, value: Number(row.total) })),
                    unit
                ),
            }
        })
    }, [data, start, end, unit])

    const totals = useMemo<ActivitySeriesTotal[]>(
        () =>
            ACTIVITY_SERIES.map((config) => ({
                ...config,
                total: (data?.[config.key] ?? []).reduce((sum, row) => sum + Number(row.total), 0),
            })),
        [data]
    )

    return { series, totals, unit, isLoading, error }
}

export const TIMELINE_PAGE_SIZE = 50

const timelinePageQuery = (applied: ActivityFilters) => {
    const { baseFilters, filterGroups } = compileActivityFilters(applied)

    return createActivityQuery({
        select: [
            "id",
            "kind",
            "subject_type",
            "project_id",
            "created_by",
            "created_at",
            "data",
        ] as const,
        filters: baseFilters,
        filterGroups,
        orderBy: [
            { field: "created_at", dir: "DESC" },
            { field: "id", dir: "DESC" },
        ] as const,
        limit: TIMELINE_PAGE_SIZE,
    })
}

const fetchTimelinePage = (
    orgId: string,
    applied: ActivityFilters,
    cursor: TimelineCursor | null
) => fetchActivityTimeline(orgId, timelinePageQuery(applied), cursor)

type TimelinePage = Awaited<ReturnType<typeof fetchTimelinePage>>

export const useActivityTimeline = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const applied = useAppSelector(selectAppliedFilters)
    const appliedKey = JSON.stringify(applied)

    const { data, size, setSize, isLoading, isValidating, error } = useSWRInfinite(
        (pageIndex, previousPage: TimelinePage | null) => {
            if (!orgId) return null
            if (pageIndex === 0) return ["activity-timeline", orgId, appliedKey, null]
            if (!previousPage?.nextCursor) return null
            return ["activity-timeline", orgId, appliedKey, JSON.stringify(previousPage.nextCursor)]
        },
        ([, , , cursor]) =>
            fetchTimelinePage(
                orgId!,
                applied,
                cursor ? (JSON.parse(cursor as string) as TimelineCursor) : null
            ),
        { revalidateOnFocus: false, revalidateOnReconnect: false, keepPreviousData: true }
    )

    const events = useMemo(() => {
        const pages = data ?? []
        const seen = new Set<string>()
        const collected: TimelinePage["events"] = []

        pages.forEach((page) => {
            for (const event of page.events) {
                if (seen.has(event.id)) continue
                seen.add(event.id)
                collected.push(event)
            }
        })

        return collected
    }, [data])

    const lastPage = data?.at(-1)
    const hasMore = !!lastPage?.nextCursor

    return {
        events,
        hasMore,
        isLoading,
        isLoadingMore: isValidating && size > 1,
        loadMore: () => setSize((current) => current + 1),
        error,
    }
}
